import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTimer } from '../contexts/TimerContext';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../components/SubscriptionGuard';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { 
  Play, 
  Pause,
  Square, 
  Plus, 
  Clock, 
  Trash2,
  MoreHorizontal,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Search,
  Edit3,
  Timer,
  TrendingUp,
  Calendar as CalendarIcon,
  AlertTriangle,
  Coffee,
  Camera
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ScreenshotGallery } from '../components/ScreenshotGallery';
import { ManualEntryDialog, EditEntryDialog, IdleAlertDialog, TimeEntryRow } from '../components/tracker/TrackerDialogs';
import { ScreenCaptureSection } from '../components/ScreenCaptureSection';
import { format, isToday, isYesterday, addDays, subDays } from 'date-fns';
import { cn } from '../lib/utils';

const TimeTracker = () => {
  const { api } = useAuth();
  const { 
    isRunning, 
    isPaused,
    elapsedTime, 
    formatTime, 
    startTimer, 
    pauseTimer,
    resumeTimer,
    stopTimer,
    discardTimer,
    runningEntry,
    updateRunningEntry,
    continueEntry,
    showIdleDialog,
    idleMinutes,
    keepIdleTime,
    discardIdleTime
  } = useTimer();
  const { t } = useTranslation();
  const { subscription } = useSubscription() || {};
  const isFreePlan = subscription?.plan === 'free';
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onLeave, setOnLeave] = useState(null);
  const [description, setDescription] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTask, setSelectedTask] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [defaultProjectId, setDefaultProjectId] = useState(null);
  
  // Stats - these now come from the summary API which merges timer + manual entries
  const [todayTotal, setTodayTotal] = useState(0);
  const [weekTotal, setWeekTotal] = useState(0);
  const [monthTotal, setMonthTotal] = useState(0);
  const [summaryLoading, setSummaryLoading] = useState(true);
  
  // Date navigation
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  
  // Manual entry
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    description: '',
    project_id: '',
    date: new Date(),
    start_time: '09:00',
    end_time: '17:00',
    notes: ''
  });
  
  // Autocomplete state
  const [recentTasks, setRecentTasks] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const descriptionRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Screenshot gallery
  const [screenshotEntryId, setScreenshotEntryId] = useState(null);
  
  // Delete confirmation
  const [entryToDelete, setEntryToDelete] = useState(null);
  
  // Edit entry
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({
    description: '',
    project_id: '',
    start_time: '',
    end_time: ''
  });
  
  // Update edit form when editingEntry changes
  useEffect(() => {
    if (editingEntry) {
      const startDate = new Date(editingEntry.start_time);
      const endDate = editingEntry.end_time ? new Date(editingEntry.end_time) : null;
      setEditForm({
        description: editingEntry.description || '',
        project_id: editingEntry.project_id || '',
        start_time: startDate.toTimeString().slice(0, 5),
        end_time: endDate ? endDate.toTimeString().slice(0, 5) : ''
      });
    }
  }, [editingEntry]);
  
  // Idle detection now handled by TimerContext
  // idleMinutes, showIdleDialog, keepIdleTime, discardIdleTime come from useTimer()

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if typing in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        if (e.key === 'Escape') {
          e.target.blur();
          setShowSuggestions(false);
        }
        return;
      }
      
      if (e.key === ' ' && !showManualEntry && !showIdleDialog) {
        e.preventDefault();
        if (!isRunning) {
          handleStartTimer();
        } else if (isPaused) {
          resumeTimer();
        } else {
          pauseTimer();
        }
      }
      
      if (e.key === 'Escape') {
        setShowManualEntry(false);
        setShowSuggestions(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isRunning, isPaused, showManualEntry, showIdleDialog]);

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  useEffect(() => {
    if (runningEntry) {
      setDescription(runningEntry.description || '');
      setSelectedProject(runningEntry.project_id || '');
      setSelectedTask(runningEntry.task_id || '');
    }
  }, [runningEntry]);

  useEffect(() => {
    if (selectedProject) {
      fetchTasks(selectedProject);
    } else {
      setTasks([]);
    }
  }, [selectedProject]);

  // Update today's stat in real-time when timer is running
  // Note: weekTotal and monthTotal from summary API already include manual entries
  // We only need to add the currently running timer's elapsed time to todayTotal
  useEffect(() => {
    if (isRunning && !isPaused) {
      const runningStartTime = runningEntry?.start_time ? new Date(runningEntry.start_time) : null;
      
      if (runningStartTime && isToday(runningStartTime)) {
        // The running timer's elapsed time is added to today's total in the UI
        // Base todayTotal already comes from the summary API and includes manual entries
      }
    }
  }, [elapsedTime, isRunning, isPaused, runningEntry]);

  const fetchData = async () => {
    try {
      // Fetch time entries, projects, profile, and summary data in parallel
      // The summary API merges timer entries AND manual timesheet entries for consistent totals
      const [entriesRes, projectsRes, profileRes, daySummaryRes, weekSummaryRes, monthSummaryRes, leaveRes] = await Promise.all([
        api.get('/time-entries'),
        api.get('/projects'),
        api.get('/profile').catch(() => ({ data: {} })),
        api.get('/reports/summary?period=day'),
        api.get('/reports/summary?period=week'),
        api.get('/reports/summary?period=month'),
        api.get('/time-entries/leave-check').catch(() => ({ data: { on_leave: false } }))
      ]);
      
      setOnLeave(leaveRes.data?.on_leave ? leaveRes.data : null);
      
      const allEntries = entriesRes.data;
      setEntries(allEntries);
      setProjects(projectsRes.data);
      
      // Set default project from user profile if no project is selected and no timer is running
      const profile = profileRes.data;
      if (profile.default_project_id) {
        setDefaultProjectId(profile.default_project_id);
        if (!selectedProject && !runningEntry) {
          setSelectedProject(profile.default_project_id);
        }
      }
      
      // Use summary API data for stats (includes both timer + manual timesheet entries)
      setTodayTotal(daySummaryRes.data.total_duration || 0);
      setWeekTotal(weekSummaryRes.data.total_duration || 0);
      setMonthTotal(monthSummaryRes.data.total_duration || 0);
      setSummaryLoading(false);
      
      // Get recent unique tasks with project info for autocomplete
      const taskMap = new Map();
      allEntries
        .filter(e => e.description) // Only entries with descriptions
        .forEach(e => {
          const key = e.description;
          // Keep the most recent entry for each unique description
          if (!taskMap.has(key) || new Date(e.start_time) > new Date(taskMap.get(key).start_time)) {
            taskMap.set(key, {
              description: e.description,
              project_id: e.project_id,
              start_time: e.start_time
            });
          }
        });
      // Convert to array and sort by most recent, limit to 10
      const recentTasksWithProjects = Array.from(taskMap.values())
        .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
        .slice(0, 10);
      setRecentTasks(recentTasksWithProjects);
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setSummaryLoading(false);
    }
  };

  const fetchTasks = async (projectId) => {
    try {
      const response = await api.get(`/tasks/project/${projectId}`);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  // Open manual entry dialog with default project preset
  const openManualEntry = () => {
    setManualEntry(prev => ({
      ...prev,
      project_id: defaultProjectId || '',
      description: '',
      date: new Date(),
      start_time: '09:00',
      end_time: '17:00',
      notes: ''
    }));
    setShowManualEntry(true);
  };

  const handleStartTimer = async () => {
    // Check if on leave
    if (onLeave) {
      toast.error(`You are on approved leave (${onLeave.leave_type || 'Time Off'}) today. Timer cannot be started.`);
      return;
    }
    // Check if timer is already running
    if (isRunning) {
      toast.error('Timer is already running. Please stop the current timer before starting a new one.');
      return;
    }
    
    // Validate required fields
    if (!description || description.trim() === '') {
      toast.error('Please enter a task description before starting the timer');
      return;
    }
    if (!selectedProject) {
      toast.error('Please select a project before starting the timer');
      return;
    }
    
    try {
      await startTimer({
        description,
        project_id: selectedProject || null,
        task_id: selectedTask || null
      });
      toast.success('Timer started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start timer');
    }
  };

  const handleStopTimer = async () => {
    try {
      // Check if elapsed time is zero (or near-zero) - auto-delete
      if (elapsedTime < 1) {
        // Silently discard the entry
        await discardTimer();
        setDescription('');
        setSelectedProject('');
        setSelectedTask('');
        // No toast - silently discard zero-duration entries
        return;
      }
      
      const result = await stopTimer();
      toast.success(`Tracked ${formatTime(result.duration)}`);
      fetchData();
      setDescription('');
      setSelectedProject('');
      setSelectedTask('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to stop timer');
    }
  };

  const handlePauseResume = () => {
    if (isPaused) {
      resumeTimer();
      toast.info('Timer resumed');
    } else {
      pauseTimer();
      toast.info('Timer paused');
    }
  };

  const handleContinueEntry = async (entry) => {
    // Check if timer is already running
    if (isRunning) {
      toast.error('Timer is already running. Please stop the current timer before continuing another task.');
      return;
    }
    
    try {
      const prevDuration = entry.duration || 0;
      await continueEntry(entry);
      setDescription(entry.description || '');
      setSelectedProject(entry.project_id || '');
      // Immediately subtract the continued entry's previous duration from local totals
      // to prevent double-counting while fetchData() is in-flight.
      // The summary API excludes running entries (is_running: false filter),
      // but until fetchData completes, todayTotal still includes the old completed duration.
      // elapsedTime already includes prevDuration (start_time is adjusted back by backend).
      if (prevDuration > 0) {
        setTodayTotal(prev => Math.max(0, prev - prevDuration));
        setWeekTotal(prev => Math.max(0, prev - prevDuration));
        setMonthTotal(prev => Math.max(0, prev - prevDuration));
      }
      fetchData();
      toast.success('Timer continued');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to continue entry';
      if (errorMessage.toLowerCase().includes('already running')) {
        toast.error('Timer is already running. Please stop the current timer first.');
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleManualEntry = async () => {
    // Validate description is required
    if (!manualEntry.description || manualEntry.description.trim() === '') {
      toast.error('Description is required');
      return;
    }

    // Validate project is required
    if (!manualEntry.project_id) {
      toast.error('Project is required for manual time entries');
      return;
    }
    
    // Validate end time is after start time
    const [startH, startM] = manualEntry.start_time.split(':').map(Number);
    const [endH, endM] = manualEntry.end_time.split(':').map(Number);
    const startMins = startH * 60 + startM;
    const endMins = endH * 60 + endM;
    
    if (endMins <= startMins) {
      toast.error('End time must be after start time');
      return;
    }
    
    try {
      const dateStr = format(manualEntry.date, 'yyyy-MM-dd');
      // Convert to proper UTC timestamps so backend queries work consistently
      const startLocal = new Date(`${dateStr}T${manualEntry.start_time}:00`);
      const endLocal = new Date(`${dateStr}T${manualEntry.end_time}:00`);
      
      await api.post('/time-entries', {
        description: manualEntry.description,
        project_id: manualEntry.project_id || null,
        start_time: startLocal.toISOString(),
        end_time: endLocal.toISOString()
      });
      
      toast.success(isFreePlan ? 'Time entry saved successfully' : 'Time entry submitted for manager approval');
      setShowManualEntry(false);
      setManualEntry({
        description: '',
        project_id: '',
        date: new Date(),
        start_time: '09:00',
        end_time: '17:00',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    }
  };

  // Edit entry handler
  const handleEditEntry = async () => {
    if (!editingEntry) return;
    
    // Validate description is required
    if (!editForm.description || editForm.description.trim() === '') {
      toast.error('Description is required');
      return;
    }
    
    // Validate times
    if (editForm.end_time) {
      const [startH, startM] = editForm.start_time.split(':').map(Number);
      const [endH, endM] = editForm.end_time.split(':').map(Number);
      const startMins = startH * 60 + startM;
      const endMins = endH * 60 + endM;
      
      if (endMins <= startMins) {
        toast.error('End time must be after start time');
        return;
      }
    }
    
    try {
      const originalDate = new Date(editingEntry.start_time).toISOString().split('T')[0];
      const startLocal = new Date(`${originalDate}T${editForm.start_time}:00`);
      const endLocal = editForm.end_time ? new Date(`${originalDate}T${editForm.end_time}:00`) : null;
      
      await api.put(`/time-entries/${editingEntry.id}`, {
        description: editForm.description,
        project_id: editForm.project_id || null,
        start_time: startLocal.toISOString(),
        end_time: endLocal ? endLocal.toISOString() : null
      });
      
      toast.success('Time entry updated');
      setEditingEntry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    }
  };

  // Idle dialog handlers — delegate to TimerContext
  const handleKeepIdleTime = () => {
    keepIdleTime();
    toast.success('Idle time kept — counted as work');
  };

  const handleDiscardIdleTime = () => {
    discardIdleTime(idleMinutes);
    toast.info(`Discarded ${idleMinutes} minute(s) of idle time`);
  };

  const handleAddAsBreak = () => {
    pauseTimer();
    toast.success('Timer paused. Idle time recorded as break.');
  };

  // Auto-stop due to prolonged idle
  const handleAutoStopDueToIdle = async () => {
    try {
      const idleSecondsToDeduct = idleMinutes * 60;
      await stopTimer(idleSecondsToDeduct);
      fetchData();
      setDescription('');
      setSelectedProject('');
      setSelectedTask('');
      toast.warning('Timer auto-stopped due to inactivity', {
        description: `${idleMinutes} minute(s) of idle time deducted`
      });
    } catch (error) {
      console.error('Failed to auto-stop timer:', error);
    }
  };

  const handleDeleteEntry = async (id) => {
    try {
      await api.delete(`/time-entries/${id}`);
      toast.success('Entry deleted');
      setEntryToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleDeleteEntryClick = (id) => {
    setEntryToDelete(id);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const formatHours = (seconds) => {
    if (!seconds || seconds === 0) return '0h 0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDateHeader = (dateString) => {
    const date = new Date(dateString);
    if (isToday(date)) return t('timeTracker.today');
    if (isYesterday(date)) return t('timeTracker.yesterday');
    return format(date, 'EEEE, MMMM d');
  };

  const formatTimeOnly = (dateString) => {
    return format(new Date(dateString), 'hh:mm a');
  };

  const getProjectById = (id) => projects.find(p => p.id === id);

  // Calculate manual entry duration
  const calculateManualDuration = () => {
    const [startH, startM] = manualEntry.start_time.split(':').map(Number);
    const [endH, endM] = manualEntry.end_time.split(':').map(Number);
    const startMins = startH * 60 + startM;
    const endMins = endH * 60 + endM;
    const diffMins = endMins - startMins;
    if (diffMins <= 0) return '0h 0m';
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  // Filter entries by selected date, then group by date
  const filteredEntries = entries.filter(entry => {
    if (!entry.start_time) return false;
    const entryDate = new Date(entry.start_time);
    return format(entryDate, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
  });

  // Group filtered entries by date
  const groupedEntries = filteredEntries.reduce((groups, entry) => {
    const date = entry.start_time?.slice(0, 10);
    if (!groups[date]) groups[date] = [];
    groups[date].push(entry);
    return groups;
  }, {});

  // Filter projects by search
  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
    (p.client_name && p.client_name.toLowerCase().includes(projectSearch.toLowerCase()))
  );

  // Autocomplete suggestions with project info
  const suggestions = recentTasks.filter(task => 
    task.description.toLowerCase().includes(description.toLowerCase()) && 
    task.description !== description
  ).slice(0, 8);

  const handleProjectChange = (value) => {
    setSelectedProject(value === 'none' ? '' : value);
  };

  // Handle selecting a suggestion from autocomplete
  const handleSelectSuggestion = (suggestion) => {
    setDescription(suggestion.description);
    if (suggestion.project_id) {
      setSelectedProject(suggestion.project_id);
    }
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    descriptionRef.current?.focus();
  };

  // Handle keyboard navigation in autocomplete
  const handleDescriptionKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && suggestions[selectedSuggestionIndex]) {
          handleSelectSuggestion(suggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      case 'Tab':
        if (selectedSuggestionIndex >= 0 && suggestions[selectedSuggestionIndex]) {
          e.preventDefault();
          handleSelectSuggestion(suggestions[selectedSuggestionIndex]);
        }
        break;
      default:
        break;
    }
  };

  const navigateDate = (direction) => {
    setSelectedDate(prev => direction === 'next' ? addDays(prev, 1) : subDays(prev, 1));
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="time-tracker-page">

        {/* Leave Banner */}
        {onLeave && (
          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/30 rounded-xl p-4 flex items-center gap-3" data-testid="on-leave-banner">
            <div className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">{t('timeTracker.onLeaveToday')}</p>
              <p className="text-xs text-amber-700 dark:text-amber-400">{t('timeTracker.onLeaveMessage')}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
              {t('nav.timeTracker')}
            </h1>
            <p className="text-slate-500 mt-1">
            </p>
          </div>
          
          <Button 
            variant="outline" 
            className="gap-2 h-10 rounded-xl font-semibold border-slate-200 hover:bg-slate-50 dark:bg-slate-700" 
            onClick={openManualEntry}
            data-testid="manual-entry-btn"
          >
            <Plus className="w-4 h-4" />
            {t('timeTracker.manualEntry')}
          </Button>
        </div>

        {/* Stats Bar - Compact */}
        <div className="grid grid-cols-3 gap-3">
          <div className="premium-card p-3" data-testid="stat-today">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('timeTracker.totalToday')}</span>
                <p className="text-xl font-bold text-slate-900 dark:text-white timer-display">
                  {formatHours(todayTotal + (isRunning && runningEntry?.start_time && isToday(new Date(runningEntry.start_time)) ? elapsedTime - (runningEntry.duration || 0) : 0))}
                </p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                <Clock className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="premium-card p-3" data-testid="stat-week">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('timeTracker.totalWeek')}</span>
                <p className="text-xl font-bold text-slate-900 dark:text-white timer-display">
                  {formatHours(weekTotal + (isRunning ? elapsedTime - (runningEntry?.duration || 0) : 0))}
                </p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
          </div>
          
          <div className="premium-card p-3" data-testid="stat-month">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('timeTracker.totalMonth')}</span>
                <p className="text-xl font-bold text-slate-900 dark:text-white timer-display">
                  {formatHours(monthTotal + (isRunning ? elapsedTime - (runningEntry?.duration || 0) : 0))}
                </p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/20 flex items-center justify-center">
                <CalendarDays className="w-4 h-4 text-violet-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Timer Card - Redesigned */}
        <div className={cn(
          "rounded-2xl overflow-hidden transition-all duration-300",
          isRunning 
            ? "bg-slate-800 shadow-lg shadow-slate-900/20 border border-slate-700" 
            : "bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900 border border-slate-200 dark:border-slate-700"
        )} data-testid="timer-card">
          <div className="p-4">
            {/* Running indicator */}
            {isRunning && (
              <div className="flex items-center gap-2 mb-3">
                <div className={cn(
                  "w-2 h-2 rounded-full",
                  isPaused ? "bg-amber-400" : "bg-emerald-400 animate-pulse"
                )} />
                <span className={cn(
                  "text-xs font-semibold uppercase tracking-wider",
                  isPaused ? "text-amber-400" : "text-emerald-400"
                )}>
                  {isPaused ? 'Paused' : 'Recording'}
                </span>
              </div>
            )}
            
            {/* Description Input - Full Width Row */}
            <div className="relative mb-3">
              <Input
                ref={descriptionRef}
                placeholder={t('timeTracker.description')}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setShowSuggestions(e.target.value.length >= 1);
                  setSelectedSuggestionIndex(-1);
                }}
                onFocus={() => {
                  if (description.length >= 1) {
                    setShowSuggestions(true);
                  }
                }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onKeyDown={handleDescriptionKeyDown}
                className={cn(
                  "h-11 text-base border-0 rounded-xl font-medium",
                  isRunning 
                    ? "bg-slate-700/50 text-white placeholder:text-slate-400 focus-visible:ring-slate-500" 
                    : "bg-white dark:bg-slate-800 placeholder:text-slate-400"
                )}
                data-testid="timer-description-input"
                required
              />
              
              {/* Autocomplete Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div 
                  ref={suggestionsRef}
                  className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden max-h-64 overflow-y-auto"
                  data-testid="autocomplete-dropdown"
                >
                  {suggestions.map((suggestion, idx) => {
                    const project = getProjectById(suggestion.project_id);
                    return (
                      <button
                        key={idx}
                        className={cn(
                          "w-full px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800 flex flex-col gap-0.5 transition-colors",
                          selectedSuggestionIndex === idx && "bg-blue-50 dark:bg-blue-900/30"
                        )}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectSuggestion(suggestion);
                        }}
                        onMouseEnter={() => setSelectedSuggestionIndex(idx)}
                        data-testid={`autocomplete-option-${idx}`}
                      >
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                          {suggestion.description}
                        </span>
                        {project && (
                          <div className="flex items-center gap-1.5">
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: project.color || '#94a3b8' }}
                            />
                            <span className="text-xs text-slate-500">{project.name}</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Project Selector + Timer Row */}
            <div className="flex items-center gap-3">
              {/* Project Dropdown - Pill Style */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className={cn(
                      "h-10 px-3 justify-between rounded-full border",
                      isRunning 
                        ? "bg-slate-700/50 border-slate-600 text-white hover:bg-slate-700" 
                        : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:bg-slate-700"
                    )}
                    data-testid="timer-project-select"
                  >
                    {selectedProject ? (
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: getProjectById(selectedProject)?.color || '#94a3b8' }}
                        />
                        <span className={cn(
                          "text-sm font-medium truncate max-w-[120px]",
                          isRunning ? "text-white" : ""
                        )}>
                          {getProjectById(selectedProject)?.name}
                        </span>
                      </div>
                    ) : (
                      <span className={isRunning ? "text-slate-400" : "text-slate-400"}>{t('timeTracker.selectProjectRequired')}</span>
                    )}
                    <ChevronRight className={cn("w-4 h-4 rotate-90 ml-1", isRunning ? "text-slate-400" : "text-slate-400")} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search projects..."
                        value={projectSearch}
                        onChange={(e) => setProjectSearch(e.target.value)}
                        className="pl-8 h-9"
                      />
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-1">
                    {projects.length === 0 ? (
                      <div className="px-3 py-4 text-center">
                        <p className="text-sm text-slate-500">{t('timeTracker.noProjectsAssigned')}</p>
                      </div>
                    ) : (
                      <>
                        {filteredProjects.map(project => (
                          <button
                            key={project.id}
                            className="w-full px-3 py-2 text-left rounded-lg hover:bg-slate-50 flex items-center gap-2"
                            onClick={() => {
                              setSelectedProject(project.id);
                              setProjectSearch('');
                            }}
                          >
                            <div 
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: project.color }}
                            />
                            <div>
                              <span className="text-sm font-medium">{project.name}</span>
                              {project.client_name && (
                                <span className="text-xs text-slate-400 block">{project.client_name}</span>
                              )}
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              
              {/* Spacer */}
              <div className="flex-1" />
              
              {/* Timer Display & Controls */}
              <div className="flex items-center gap-3">
                <div className={cn(
                  "timer-display text-3xl font-bold tracking-tight transition-colors font-mono",
                  isRunning 
                    ? isPaused 
                      ? "text-amber-400" 
                      : "text-white"
                    : "text-slate-300 dark:text-slate-600"
                )} data-testid="timer-display">
                  {formatTime(isRunning ? elapsedTime : 0)}
                </div>
                
                {!isRunning ? (
                  <Button
                    onClick={handleStartTimer}
                    size="lg"
                    disabled={!description?.trim() || !selectedProject}
                    className="h-12 px-6 rounded-full bg-emerald-600 dark:bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 font-semibold gap-2 disabled:bg-slate-200 disabled:dark:bg-slate-700 disabled:text-slate-400 disabled:dark:text-slate-500 disabled:shadow-none disabled:cursor-not-allowed"
                    data-testid="start-timer-btn"
                  >
                    <Play className="w-5 h-5" />
                    {t('timeTracker.startTime')}
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handlePauseResume}
                      size="lg"
                      className={cn(
                        "w-11 h-11 rounded-full p-0 transition-all",
                        isPaused 
                          ? "bg-slate-700 hover:bg-slate-600 text-white border border-slate-600"
                          : "bg-slate-700 hover:bg-slate-600 text-white border border-slate-600"
                      )}
                      data-testid="pause-timer-btn"
                    >
                      {isPaused ? <Play className="w-5 h-5 ml-0.5" /> : <Pause className="w-5 h-5" />}
                    </Button>
                    
                    <Button
                      onClick={handleStopTimer}
                      size="lg"
                      className="h-11 px-5 rounded-full bg-red-600 dark:bg-red-600 hover:bg-red-700 dark:hover:bg-red-500 text-white font-semibold"
                      data-testid="stop-timer-btn"
                    >
                      <Square className="w-4 h-4 mr-1.5" />
                      Stop
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Screen Capture Section - Fix 48: Moved here from floating panel */}
        <ScreenCaptureSection />

        {/* Date Navigation - Priority 2.5 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0 rounded-lg"
              onClick={() => navigateDate('prev')}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <Popover open={showCalendar} onOpenChange={setShowCalendar}>
              <PopoverTrigger asChild>
                <Button variant="ghost" className="h-8 px-3 rounded-lg font-medium">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {isToday(selectedDate) ? t('timeTracker.today') : format(selectedDate, 'MMM d, yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) setSelectedDate(date);
                    setShowCalendar(false);
                  }}
                />
              </PopoverContent>
            </Popover>
            
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0 rounded-lg"
              onClick={() => navigateDate('next')}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            
            {!isToday(selectedDate) && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 px-3 rounded-lg text-blue-600"
                onClick={goToToday}
              >
                Today
              </Button>
            )}
          </div>
        </div>

        {/* Time Entries List - Priority 1.3 */}
        <div className="space-y-4" data-testid="time-entries-list">
          {Object.entries(groupedEntries).map(([date, dayEntries]) => {
            const totalDuration = dayEntries.reduce((acc, e) => acc + (e.duration || 0), 0);
            
            return (
              <div key={date} className="premium-card overflow-hidden">
                {/* Date Header with Total */}
                <div className="flex items-center justify-between px-5 py-3 bg-slate-50 dark:bg-slate-700/80 dark:bg-slate-800/30 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-3">
                    <CalendarDays className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {formatDateHeader(date)}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-slate-900 dark:text-white timer-display">
                    {formatDuration(totalDuration)}
                  </span>
                </div>
                
                {/* Entries */}
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {dayEntries.map((entry) => (
                    <TimeEntryRow
                      key={entry.id}
                      entry={entry}
                      project={getProjectById(entry.project_id)}
                      onContinue={handleContinueEntry}
                      onEdit={setEditingEntry}
                      onDelete={handleDeleteEntryClick}
                      onViewScreenshots={setScreenshotEntryId}
                      formatDuration={formatDuration}
                      formatTimeOnly={formatTimeOnly}
                    />
                  ))}
                </div>
              </div>
            );
          })}
          
          {/* Empty State */}
          {Object.keys(groupedEntries).length === 0 && !loading && (
            <div className="premium-card">
              <div className="py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                  <Timer className="w-8 h-8 text-slate-300 dark:text-slate-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                  {t('timeTracker.noEntriesToday')}
                </h3>
                <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
                  {isToday(selectedDate) 
                    ? t('timeTracker.startTracking')
                    : t('timeTracker.noEntriesToday')}
                </p>
                {isToday(selectedDate) && (
                  <div className="flex items-center justify-center gap-3">
                    <Button 
                      onClick={handleStartTimer}
                      className="btn-premium"
                      disabled={isRunning || !description?.trim() || !selectedProject}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {t('timeTracker.startTimer')}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={openManualEntry}
                    className="rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('timeTracker.manualEntry')}
                  </Button>
                </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Manual Entry Modal */}
        <ManualEntryDialog
          open={showManualEntry}
          onClose={setShowManualEntry}
          manualEntry={manualEntry}
          setManualEntry={setManualEntry}
          projects={projects}
          onSave={handleManualEntry}
          calculateDuration={calculateManualDuration}
        />
        
        {/* Edit Entry Dialog */}
        <EditEntryDialog
          entry={editingEntry}
          editForm={editForm}
          setEditForm={setEditForm}
          projects={projects}
          onSave={handleEditEntry}
          onClose={() => setEditingEntry(null)}
        />
        
        {/* Screenshot Gallery */}
        <ScreenshotGallery
          timeEntryId={screenshotEntryId}
          isOpen={!!screenshotEntryId}
          onClose={() => setScreenshotEntryId(null)}
        />
        
        {/* Idle Time Detection Dialog */}
        <IdleAlertDialog
          open={showIdleDialog}
          onOpenChange={() => {}}
          idleTime={idleMinutes * 60}
          formatHours={formatHours}
          onKeep={handleKeepIdleTime}
          onDiscard={handleDiscardIdleTime}
          onBreak={handleAddAsBreak}
        />

        {/* Delete Entry Confirmation Dialog */}
        <AlertDialog open={!!entryToDelete} onOpenChange={(open) => !open && setEntryToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete time entry?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this time entry? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-entry-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDeleteEntry(entryToDelete)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-entry-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default TimeTracker;
